import { observable, reaction, runInAction } from "mobx";

import { BaseSignalRCoreHubStore } from "./core/BaseSignalRCoreHubStore";
import type { RootStore } from "@state/stores/types";

import type { HubConnection } from "@microsoft/signalr";
import { MatchPoints } from "@api-types/score-predictor/ScorePredictorApiTypes";

export type ShortGuid = string;
export type LeagueId = number;

export interface Score {
	home: number;
	away: number;
}

export interface MatchTime {
	displayTime: string | null;
	additionalInformation: string | null;
	regularTime: string;
	remainingTime: string;
	remainingTimeInPeriod: string;
	stoppageTime: string;
}

export interface EventState {
	eventId: ShortGuid;
	/** The one score, full-time-first (running while live, frozen once `isFinished`),
	 *  or null. Delta-merge: a null `score` on a push means "keep the prior value". */
	score: Score | null;
	matchTime: MatchTime | null;
	isFinished: boolean;
	/** The caller's points for this event — present only on the per-connection
	 *  GetEventStates pull (resync), never on the shared EventLiveUpdate broadcast. */
	points?: MatchPoints | null;
}

export interface EventPointsUpdate {
	eventId: ShortGuid;
	points: MatchPoints;
}

export interface StandingsUpdate {
	leagueId: LeagueId;
	recalculatedAt: string;
}

export interface SubscriptionRegistration {
	eventIds: ShortGuid[];
	leagueId: LeagueId | null;
	isActive: boolean;
}

const EVENT_IDS_CAP = 200;
const DEBOUNCE_MS = 50;
// Hold the connection briefly after the last registration drops, so navigating
// between score-predictor screens (dispose-then-register) doesn't churn a
// disconnect/reconnect — a follow-up register() inside this window cancels it.
const TEARDOWN_DELAY_MS = 3000;

export class ScorePredictorHubStore extends BaseSignalRCoreHubStore {
	@observable public lastStandingsRecalculated: StandingsUpdate | null = null;
	/** Set when more than EVENT_IDS_CAP eventIds are requested across all
	 *  registrations and the hub had to trim. Consumers (UI banners) can
	 *  observe this to surface a "live updates limited" indicator. */
	@observable public subscriptionOverflow: {
		desired: number;
		cap: number;
	} | null = null;

	private registrations = new Map<number, () => SubscriptionRegistration>();
	private registrationDisposers = new Map<number, () => void>();
	private nextRegistrationId = 0;
	private debounceTimer: ReturnType<typeof setTimeout> | null = null;
	private teardownTimer: ReturnType<typeof setTimeout> | null = null;
	private currentSubscriptionKey = "";

	constructor(rootStore: RootStore) {
		super(rootStore);
	}

	protected hubUrl(): string {
		// Feature gate: with the flag off the hub must never connect, even if a
		// score-predictor route is reached directly. An empty URL makes the base
		// initialize() bail, so register() becomes a no-op on the connection side.
		if (!WEBPACK_IS_SCORE_PREDICTOR_ENABLED) {
			return "";
		}
		const base = WEBPACK_SCORE_PREDICTOR_BASE_ADDRESS;
		if (!base) {
			return "";
		}
		return `${base}/hub`;
	}

	protected registerHandlers(connection: HubConnection): void {
		// Identity-guard every handler so frames buffered on a closing
		// connection don't write into shared state after the hub has
		// moved on to a new connection.
		connection.on("EventLiveUpdate", (state: EventState) => {
			if (connection !== this.connection) return;
			this.rootStore.scorePredictorLiveState.mergeEventState(state);
		});

		connection.on(
			"EventPointsUpdate",
			(update: EventPointsUpdate) => {
				if (connection !== this.connection) return;
				this.rootStore.scorePredictorLiveState.setLatestPoints(
					update.eventId,
					update.points
				);
			}
		);

		connection.on("StandingsRecalculated", (update: StandingsUpdate) => {
			if (connection !== this.connection) return;
			runInAction(() => {
				this.lastStandingsRecalculated = update;
			});
		});
	}

	protected onStopped(): void {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = null;
		}
		if (this.teardownTimer) {
			clearTimeout(this.teardownTimer);
			this.teardownTimer = null;
		}
		this.currentSubscriptionKey = "";
		runInAction(() => {
			this.subscriptionOverflow = null;
		});
	}

	/** No @action.bound: the base destroyConnectionOnUserLogout is plain (its
	 *  only mutation routes through stop()). super-call resolves through the
	 *  prototype without installing an own-property on `this`, so the override
	 *  stays addressable across repeated logouts. */
	public async destroyConnectionOnUserLogout(): Promise<void> {
		await super.destroyConnectionOnUserLogout();
		// latestPoints is per-user, eventStates is shared but may contain
		// stale data the next user shouldn't see on first paint before resync.
		this.rootStore.scorePredictorLiveState.reset();
	}

	protected async onStarted(connection: HubConnection): Promise<void> {
		// Initial subscribe + baseline fetch on every fresh connection. Runs
		// inside the deduped startInFlight body in the base, so concurrent
		// start() callers share a single execution.
		await this.resync(connection);
	}

	protected async onReconnected(connection: HubConnection): Promise<void> {
		// Per-connection state on the server resets on reconnect.
		await this.resync(connection);
	}

	private async resync(connection: HubConnection): Promise<void> {
		// Per-connection server state was wiped (new connection or reconnect):
		// drop the dedup key so the flush actually sends, and clear the
		// per-connection projected-points cache (per-user per contract). We
		// intentionally do NOT clear eventStates here — delta-merge from
		// GetEventStates re-asserts authoritative fields, and stale entries the
		// server has pruned are dropped below (ids asked-about but not returned).
		this.currentSubscriptionKey = "";
		const liveState = this.rootStore.scorePredictorLiveState;
		liveState.clearLatestPoints();

		const eventIds = await this.flushSubscriptionInternal(connection);
		if (eventIds.length === 0) return;

		const states = await this.invokeGetEventStates(connection, eventIds);
		const returned = new Set(states.map((s) => s.eventId));
		const asked = new Set(eventIds);
		// Batch the prune + merge so observer reactions fire once for the
		// entire resync, not once per delete and once per merge.
		runInAction(() => {
			// Remove eventStates for ids we asked about but the server no
			// longer has — prevents stale entries from sticking around.
			// Snapshot keys first; mutating the map while iterating its
			// own iterator is unsafe in MobX 4.
			for (const id of Array.from(liveState.eventStates.keys())) {
				if (asked.has(id) && !returned.has(id)) {
					liveState.eventStates.delete(id);
				}
			}
			states.forEach((s) => liveState.mergeEventState(s));
		});
	}

	public register(
		getDesiredState: () => SubscriptionRegistration
	): () => void {
		const id = ++this.nextRegistrationId;

		this.registrations.set(id, getDesiredState);

		const dispose = reaction(
			() => {
				const { eventIds, leagueId, isActive } = getDesiredState();
				const sorted = [...eventIds].filter(Boolean).sort().join(",");
				return `${isActive ? "1" : "0"}|${leagueId ?? ""}|${sorted}`;
			},
			() => this.scheduleSubscriptionFlush(),
			{ fireImmediately: true }
		);
		this.registrationDisposers.set(id, dispose);

		this.ensureConnected();

		return () => this.unregister(id);
	}

	private unregister(id: number) {
		this.registrationDisposers.get(id)?.();
		this.registrationDisposers.delete(id);
		this.registrations.delete(id);

		if (this.registrations.size === 0) {
			this.scheduleTeardown();
		} else {
			this.scheduleSubscriptionFlush();
		}
	}

	/** Connect the hub when a score-predictor screen first needs it. A pending
	 *  teardown means the connection is still alive from a screen we just left —
	 *  cancel it and reuse, rather than reconnecting. */
	private ensureConnected(): void {
		if (this.teardownTimer) {
			clearTimeout(this.teardownTimer);
			this.teardownTimer = null;
			return;
		}
		if (this.registrations.size === 1) {
			this.initialize().catch(() => {
				/* failure surfaced via `state` */
			});
		}
	}

	/** Debounced disconnect once the last score-predictor screen unmounts. */
	private scheduleTeardown(): void {
		if (this.teardownTimer) {
			clearTimeout(this.teardownTimer);
		}
		this.teardownTimer = setTimeout(async () => {
			this.teardownTimer = null;
			if (this.registrations.size !== 0) return;
			await this.deactivate();
			// A screen may have re-registered during the async stop — reconnect
			// now that it has settled, preserving the safe stop-then-start order
			// (a concurrent start racing the stop can be orphaned by the epoch guard).
			if (this.registrations.size > 0) {
				this.initialize().catch(() => {
					/* failure surfaced via `state` */
				});
			}
		}, TEARDOWN_DELAY_MS);
	}

	private scheduleSubscriptionFlush() {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}
		this.debounceTimer = setTimeout(() => {
			this.debounceTimer = null;
			if (this.connection && this.state === "Connected") {
				this.flushSubscriptionInternal(this.connection).catch((err) => {
					console.error("UpdateSubscriptions flush failed:", err);
				});
			}
		}, DEBOUNCE_MS);
	}

	private async flushSubscriptionInternal(
		connection: HubConnection
	): Promise<ShortGuid[]> {
		const eventIdSet = new Set<ShortGuid>();
		let leagueId: LeagueId | null = null;

		for (const getState of this.registrations.values()) {
			const reg = getState();
			if (!reg.isActive) continue;
			for (const id of reg.eventIds) {
				if (id) eventIdSet.add(id);
			}
			if (reg.leagueId != null) {
				leagueId = reg.leagueId;
			}
		}

		const desiredCount = eventIdSet.size;
		let eventIds = Array.from(eventIdSet).sort();
		const overflowNext: { desired: number; cap: number } | null =
			eventIds.length > EVENT_IDS_CAP
				? { desired: desiredCount, cap: EVENT_IDS_CAP }
				: null;
		if (overflowNext) {
			console.warn(
				`[ScorePredictorHub] eventIds (${eventIds.length}) exceeds cap ${EVENT_IDS_CAP}; trimming to fit. Excess events will not receive live updates.`
			);
			eventIds = eventIds.slice(0, EVENT_IDS_CAP);
		}

		const key = `${leagueId ?? ""}|${eventIds.join(",")}`;
		if (key === this.currentSubscriptionKey) {
			return eventIds;
		}
		this.currentSubscriptionKey = key;

		try {
			await connection.invoke("UpdateSubscriptions", {
				eventIds,
				leagueId,
			});
			// Commit the overflow signal only after the server accepted the
			// payload — otherwise UI banner would diverge from the server's
			// actual subscription set on a failed invoke.
			runInAction(() => {
				this.subscriptionOverflow = overflowNext;
			});
		} catch (err) {
			// Reset so the next genuine registration change can retry.
			this.currentSubscriptionKey = "";
			console.error("UpdateSubscriptions failed:", err);
		}
		return eventIds;
	}

	private async invokeGetEventStates(
		connection: HubConnection,
		ids: ShortGuid[]
	): Promise<EventState[]> {
		const chunks: ShortGuid[][] = [];
		for (let i = 0; i < ids.length; i += EVENT_IDS_CAP) {
			chunks.push(ids.slice(i, i + EVENT_IDS_CAP));
		}
		const results = await Promise.all(
			chunks.map((chunk) =>
				connection
					.invoke<EventState[]>("GetEventStates", chunk)
					.catch((err) => {
						console.error("GetEventStates chunk failed:", err);
						return [] as EventState[];
					})
			)
		);
		return results.flat();
	}

	private clearTimers() {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = null;
		}

		if (this.teardownTimer) {
			clearTimeout(this.teardownTimer);
			this.teardownTimer = null;
		}
	}

	private unregisterAll() {
		for (const id of Array.from(this.registrations.keys())) {
			this.unregister(id);
		}
	}

	public async onDispose(): Promise<void> {
		this.unregisterAll();
		this.clearTimers();

		this.currentSubscriptionKey = "";

		runInAction(() => {
			this.lastStandingsRecalculated = null;
			this.subscriptionOverflow = null;
		});

		await this.deactivate();
	}
}
